<template>
  <div class="roles">
    <div class="titleRow">
      <span id="titleAndProjectName">
        <h1 id="title">{{ $t(localeConsts.rolesTitle) }}</h1>
        <span id="projectName"> {{ getProjectName }} </span>
      </span>
    </div>
    <div>
      <template>
        <div v-if="dataFinishedLoading" class="roles">
          <div class="dataTable">
            <b-table class="rolesTable w-auto" :items="processedData" :fields="columns" striped sticky-header>
              <template #cell(weekNumber)="data">
                <span>{{ data.item.weekNumber }}</span>
              </template>

              <template #cell(user)="data">
                <span>{{ `${data.item.user.first_name} ${data.item.user.last_name}` }}</span>
              </template>

              <template #cell(role)="data">
                <span>{{ getRoleName(data.item.user_role, $i18n.locale) }}</span>
              </template>

              <template #cell(attribution_date)="data">
                <span>{{ formatDate(data.item.attribution_date) }}</span>
              </template>

              <template #cell(delete)="data">
                <b-button
                  v-if="!getIsSupervisor"
                  id="deleteButton"
                  pill
                  size="sm"
                  :disabled="isWeekSameOrOneBefore(data)"
                  @click="promptToConfirmDelete(data)"
                >
                  <i class="bi bi-trash-fill icon editButtons"></i>
                </b-button>
              </template>
            </b-table>
            <b-modal
              id="delete-confirmation-modal"
              title="Confirm Deletion"
              ok-title="Yes"
              cancel-title="No"
              ok-variant="danger"
              cancel-variant="secondary"
              @ok="confirmDelete"
              @cancel="cancelDelete"
            >
              <p class="my-4">Are you sure you want to delete this role?</p>
            </b-modal>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import mapGettersMixin from '../../../mixins/mapGettersMixin'
import axios from '@/axios'
import moment from 'moment'
import { apiFetchMethodsMixin } from '@/components/mixins/apiFetchMethodsMixin'
import { generalHelperMethodsMixin } from '@/components/mixins/generalHelperMethodsMixin'
import getBrowserLocale from '../../../../util/i18n/get-browser-locale'
import { editableTableMixin } from '@/components/mixins/editableTableMixin'

export default {
  name: 'RolesView',
  mixins: [apiFetchMethodsMixin, editableTableMixin, generalHelperMethodsMixin, mapGettersMixin],

  data() {
    return {
      localeConsts: {
        roleAttributionDateColumn: 'ROLES.ATTRIBUTION_DATE',
        roleAssignedUserColumn: 'ROLES.ASSIGNED_USER',
        roleDescriptionColumn: 'ROLES.DESCRIPTION',
        roleDescriptionPlaceholder: 'ROLES.DESCRIPTION_PLACEHOLDER',
        rolesTitle: 'ROLES.TITLE',
        viewRolesCurrentSprint: 'ROLES.VIEW_ROLES_CURRENT_SPRINT'
      },
      rawRoleAttributionHistory: [],
      roles: [],
      dataFinishedLoading: false,
      deleteCandidate: null
    }
  },

  computed: {
    columns() {
      return [
        {
          label: 'PW',
          key: 'weekNumber',
          thClass: 'text-center studentTableThStyle',
          tdClass: 'text-center workReportsNumberCell'
        },
        {
          label: this.$i18n.t(this.localeConsts.roleAssignedUserColumn),
          key: 'user',
          thClass: 'text-center studentTableThStyle',
          tdClass: 'text-center workReportsNumberCell'
        },
        {
          label: this.$i18n.t(this.localeConsts.roleDescriptionColumn),
          key: 'role',
          tdClass: 'tdTextArea text-center',
          thClass: 'text-center studentTableThStyle',
          placeholder: this.$i18n.t(this.localeConsts.roleDescriptionPlaceholder)
        },
        {
          label: this.$i18n.t(this.localeConsts.roleAttributionDateColumn),
          key: 'attribution_date',
          thClass: 'text-center studentTableThStyle',
          tdClass: 'text-center workReportsNumberCell'
        },
        { key: 'delete', label: '', tdClass: 'text-center', thClass: 'text-center studentTableThStyle' }
      ]
    },

    processedData() {
      if (!this.roles.length) return []
      return this.rawRoleAttributionHistory
        .filter((entry) => entry.user.groups.some((group) => group.name === 'Students'))
        .map((entry) => ({
          ...entry,
          weekNumber: this.getWeekNumber(entry.attribution_date),
          user: entry.user,
          role: entry.user_role,
          isDelete: false
        }))
        .sort((a, b) => moment(b.attribution_date) - moment(a.attribution_date))
    }
  },

  async created() {
    this.dataFinishedLoading = await this.fetchUserRoleAttributions()
  },

  async mounted() {
    await Promise.all([this.fetchUserRoleAttributions(), this.fetchRoles()])
  },

  methods: {
    getBrowserLocale,
    async fetchUserRoleAttributions() {
      try {
        const responseProjectId = await this.getProjectId
        const params = {
          params: {
            project_id: responseProjectId
          }
        }
        const response = await axios.get('user-role-attributions', params)
        this.rawRoleAttributionHistory = response.data
        return true
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching data:', error)
      }
    },

    formatDate(date) {
      const locale = this.getLocale()
      const options = { year: 'numeric', month: 'short', day: 'numeric' }
      return new Intl.DateTimeFormat(locale, options).format(new Date(date)).replace(',', '')
    },

    getLocale() {
      return this.$i18n.locale
    },

    isWeekSameOrOneBefore(entry) {
      return !(
        entry.item.weekNumber === this.getWeekNumber(moment(new Date())) ||
        entry.item.weekNumber >= this.getWeekNumber(moment(new Date())) - 2
      )
    },

    promptToConfirmDelete(entry) {
      this.deleteCandidate = entry
      this.$bvModal.show('delete-confirmation-modal')
    },

    confirmDelete() {
      if (this.deleteCandidate) {
        this.deleteRow(this.deleteCandidate)
        this.deleteCandidate = null
      }
    },

    cancelDelete() {
      this.deleteCandidate = null
    },

    async deleteRow(data) {
      await axios.delete(`user-role-attributions/${data.item.id}/`)
      const index = this.rawRoleAttributionHistory.findIndex((entry) => entry.id === data.item.id)
      if (index !== -1) {
        this.rawRoleAttributionHistory.splice(index, 1)
      }
      this.$bvModal.hide('delete-confirmation-model')
    }
  }
}
</script>

<style lang="scss" scoped>
$text-color: #8b69a9;

.roles {
  width: 100%;
  color: $text-color;
}

.b-table-sticky-header {
  overflow-y: auto;
  max-height: 600px;
}

.titleRow {
  color: $text-color;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

#titleAndProjectName {
  display: flex;
  align-items: baseline;
}

#title {
  color: $text-color;
}

#projectName {
  margin-left: 20px;
  font-size: 30px;
}

.editButtons {
  font-size: 16px;
  color: white;
}

#deleteButton {
  margin-left: 1rem;
}
</style>
